import { HEADER_HEIGHT } from '@/utils/constants';
import { arrayRange } from '@/utils/functional/arrayRange';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React, { RefObject } from 'react';

const usePageIsOversized = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [isOversized, setIsOversized] = React.useState(true);

  const checkIsOversized = React.useCallback(() => {
    if (isMobile) return;

    const DOCUMENT_HEIGHT = document.documentElement.clientHeight - HEADER_HEIGHT;
    const CONTENT_HEIGHT = containerRef?.current?.offsetHeight || 0;

    setIsOversized(CONTENT_HEIGHT > DOCUMENT_HEIGHT);
  }, [isMobile]);

  React.useLayoutEffect(() => {
    checkIsOversized();
  }, [checkIsOversized]);

  React.useLayoutEffect(() => {
    window.addEventListener('resize', checkIsOversized);

    return () => window.removeEventListener('resize', checkIsOversized);
  }, [checkIsOversized]);

  return { containerRef, isOversized };
};

const useScrollSticky = (hasScrollEffect: boolean) => {
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (hasScrollEffect) {
      contentRef?.current?.classList?.add('is-sticky');
      return;
    }

    contentRef?.current?.classList?.remove('is-sticky');
  }, [hasScrollEffect]);

  return { contentRef };
};

interface IScrollEffectProps {
  hasScrollEffect: boolean;
  containerRef: RefObject<HTMLDivElement>;
  tabsLength: number;
  setTabIndex(index: number): void;
}

const useScrollEffect = ({
  hasScrollEffect,
  containerRef,
  tabsLength,
  setTabIndex,
}: IScrollEffectProps) => {
  const { height: containerHeight } = containerRef?.current?.getBoundingClientRect() || {
    height: 0,
  };
  const [isIntersecting, setIsIntersecting] = React.useState(false);

  // Intersection Observer to identify if the container is shown or not.
  const callbackObserver = React.useCallback((entries: any) => {
    const [entry] = entries;
    setIsIntersecting(entry.isIntersecting);
  }, []);

  React.useEffect(() => {
    const options = {
      root: null,
      rootMargin: `${HEADER_HEIGHT}px 0px 0px 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver(callbackObserver, options);
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [callbackObserver, containerRef]);

  // TabPosition calculation to make a relation between Scroll <> TabIndex
  const tabsScrollRangePositions = React.useMemo(() => {
    if (!tabsLength) return [];

    const realTabLength = containerHeight / tabsLength;

    // Last tab should be twice size to simulate a smooth scroll event.
    const lastTabLength = realTabLength + realTabLength / 2;

    const tabLength =
      tabsLength > 1 ? (containerHeight - lastTabLength) / (tabsLength - 1) : realTabLength;

    // tab position, min and max of container top position
    return arrayRange(tabsLength).map((_, index) => {
      if (tabsLength - 1 === index) return [index, Math.ceil(index * tabLength)];
      return [index, Math.ceil(index * tabLength), Math.ceil((index + 1) * tabLength)];
    });
  }, [tabsLength, containerHeight]);

  // Based on Scroll the TabIndex is set.
  const scrollHandler = React.useCallback(() => {
    if (!hasScrollEffect) return;
    if (!tabsLength) return;
    if (!tabsScrollRangePositions.length) return;

    const { top } = containerRef?.current?.getBoundingClientRect() || {
      top: 0,
    };

    if (Math.ceil(top) - HEADER_HEIGHT > 0) return;

    const topFixed = Math.ceil(-top) + HEADER_HEIGHT;
    const [tabSelected] = tabsScrollRangePositions.find((value) => {
      if (value.length === 3 && topFixed >= value[1] && topFixed < value[2]) return true;
      if (value.length === 3) return false;
      if (topFixed >= value[1]) return true;

      return false;
    }) || [0];
    setTabIndex(tabSelected);
  }, [hasScrollEffect, tabsLength, tabsScrollRangePositions, containerRef, setTabIndex]);

  React.useEffect(() => {
    if (!isIntersecting) return () => document.removeEventListener('scroll', scrollHandler);

    document.addEventListener('scroll', scrollHandler);

    return () => document.removeEventListener('scroll', scrollHandler);
  }, [scrollHandler, isIntersecting]);

  // Set TabPosition based on scroll.
  const setTabPosition = React.useCallback(
    (index = 0) => {
      if (!tabsScrollRangePositions.length) return;

      const { top } = containerRef?.current?.getBoundingClientRect() || {
        top: 0,
      };
      const topFixed = Math.ceil(top) - HEADER_HEIGHT;
      const range = tabsScrollRangePositions.find((value) => value[0] === index) || [0, 0, 0];
      const y = topFixed + window.scrollY + range[1];

      window.scroll({
        top: y,
      });
    },
    [containerRef, tabsScrollRangePositions],
  );

  return { setTabPosition };
};

export const useScrollNavigation = ({ tabsLength = 0, setTabIndex = (index: number) => {} }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { containerRef, isOversized } = usePageIsOversized();
  const hasScrollEffect = React.useMemo(() => !isOversized && !isMobile, [isMobile, isOversized]);
  const { contentRef } = useScrollSticky(hasScrollEffect);
  const { setTabPosition } = useScrollEffect({
    hasScrollEffect,
    containerRef,
    tabsLength,
    setTabIndex,
  });

  return { containerRef, contentRef, hasScrollEffect, setTabPosition };
};
